import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { useTranslation } from 'react-i18next';
import EnterprisesEmptyState from '../../components/EnterprisesEmptyState';
import { useNavigate } from 'react-router-dom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './data';
import { BODY_CONTENT_GENERATE } from './components/EnterprisesDealTableCells';
import { Enterprise } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import { getEnterpriseRoute, getIsArrayEmpty } from 'utils';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMatchMedia from 'hooks/useMatchMedia';

import './EntrprisesList.styles.scss';

type EntrprisesListProps = {
  className?: string;
};

const EntrprisesList: React.FC<EntrprisesListProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [orderedColumns, setOrderedColumns] = useState(TABLE_COLUMNS);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const {
    params: { page },
  } = useQueryParams();

  const { popServerError } = useContext(PopNotificationsContext);

  const isEmpty = !isLoading && getIsArrayEmpty(enterprises);

  const classes = classNames('enterprises-list', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const onReorderL = useCallback(async (c) => setOrderedColumns(c), []);

  const renderedContent = useMemo(
    () =>
      orderedColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (enterprise: Enterprise) =>
            BODY_CONTENT_GENERATE[col]({
              enterprise,
            }),
        }),
        {},
      ),
    [orderedColumns],
  );

  const table = useMemo(
    () => (
      <ReorderResizeTable
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        columns={orderedColumns as any}
        sortingValues={SORTING_VALUES}
        isDataLoading={isLoading}
        totalPages={totalPages}
        data={enterprises}
        customSizes={CUSTOM_COL_SIZES}
        onReorder={onReorderL}
        onRowClick={({ _id, isRWC, isDemo }) =>
          navigate(getEnterpriseRoute(isRWC, isDemo, _id, 'analytics'))
        }
      />
    ),
    [
      enterprises,
      isLoading,
      orderedColumns,
      renderedContent,
      totalPages,
      onReorderL,
      navigate,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.enterprises.getEnterprises({
          $page: page,
          $limit: limit,
        });
        setTotalPages(totalPages);
        setEnterprises(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, limit, popServerError]);

  return (
    <div className={classes}>
      <div className="enterprises-list__heading">
        <h1>{t('manageEnterprises')}</h1>
        {!isEmpty && (
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
        )}
        <Button icon={PluscircleIcon} onClick={() => navigate('add')}>
          {t(isPhablet ? 'enterprise' : 'addAnEnterprise')}
        </Button>
      </div>

      {isLoading && <Loader />}
      {isEmpty ? <EnterprisesEmptyState /> : table}
    </div>
  );
};

export default EntrprisesList;
